import { Box, Typography, Card, CardContent, CardMedia } from '@mui/material'
import {
  PlayArrow,
  TextSnippet,
  Lightbulb,
  LocationOn,
  Image as ImageIcon,
  Videocam,
  AudioFile,
  Schedule,
  PauseCircle,
} from '@mui/icons-material'
import { SessionNodeDto } from '@api/types/session'
import { WorkflowNodeType } from '../../../types/workflow.types'

interface AutoAdvanceNodeProps {
  node: SessionNodeDto
}

export default function AutoAdvanceNode({ node }: AutoAdvanceNodeProps) {
  const nodeType = node.nodeType as WorkflowNodeType

  // Render based on node type
  switch (nodeType) {
    case WorkflowNodeType.Start:
      return <StartNodeContent node={node} />

    case WorkflowNodeType.FormattedMessage:
      return <FormattedMessageContent node={node} />

    case WorkflowNodeType.TextClue:
      return <TextClueContent node={node} />

    case WorkflowNodeType.PoiLocation:
      return <PoiLocationContent node={node} />

    case WorkflowNodeType.ImageDisplay:
      return <ImageDisplayContent node={node} />

    case WorkflowNodeType.VideoPlayer:
      return <VideoPlayerContent node={node} />

    case WorkflowNodeType.AudioPlayer:
      return <AudioPlayerContent node={node} />

    case WorkflowNodeType.TimeModifier:
      return <TimeModifierContent node={node} />

    case WorkflowNodeType.TimerControl:
      return <TimerControlContent node={node} />

    default:
      return <GenericNodeContent node={node} />
  }
}

// Start node - simple welcome/intro
function StartNodeContent({ node }: { node: SessionNodeDto }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 4,
      }}
    >
      <PlayArrow sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h4" gutterBottom>
        {node.label || 'Adventure Starting...'}
      </Typography>
      {node.description && (
        <Typography variant="body1" color="text.secondary">
          {node.description}
        </Typography>
      )}
    </Box>
  )
}

// Formatted message - rich HTML content
function FormattedMessageContent({ node }: { node: SessionNodeDto }) {
  return (
    <Box sx={{ p: 2 }}>
      {node.label && (
        <Typography variant="h5" gutterBottom>
          {node.label}
        </Typography>
      )}
      {node.formattedContent ? (
        <Typography
          component="div"
          sx={{
            '& p': { marginBottom: 1 },
            '& p:last-child': { marginBottom: 0 },
            '& h1, & h2, & h3, & h4, & h5, & h6': { marginTop: 2, marginBottom: 1 },
            '& ul, & ol': { marginBottom: 1, paddingLeft: 3 },
            '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
            '& table': { borderCollapse: 'collapse', width: '100%', my: 1 },
            '& td, & th': { p: 1, verticalAlign: 'top' },
            '& table:not([data-borderless="true"]) td, & table:not([data-borderless="true"]) th': { border: '1px solid', borderColor: 'divider' },
            '& table:not([data-borderless="true"]) th': { bgcolor: 'action.hover', fontWeight: 600 },
          }}
          dangerouslySetInnerHTML={{ __html: node.formattedContent }}
        />
      ) : (
        <Typography variant="body1" color="text.secondary">
          {node.description || 'No content available'}
        </Typography>
      )}
    </Box>
  )
}

// Clue Gate - notifies player that new clues are available
function TextClueContent({ node }: { node: SessionNodeDto }) {
  return (
    <Card sx={{ maxWidth: 600, mx: 'auto' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Lightbulb color="warning" />
          <Typography variant="h6">New Clues Available!</Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1rem',
            lineHeight: 1.6,
            bgcolor: 'action.hover',
            p: 2,
            borderRadius: 1,
            borderLeft: 4,
            borderColor: 'warning.main',
          }}
        >
          New clues have been unlocked. Tap the lightbulb icon in the header to request a clue when you need help.
        </Typography>
      </CardContent>
    </Card>
  )
}

// POI location - show location info (map is in separate panel)
function PoiLocationContent({ node }: { node: SessionNodeDto }) {
  const poi = node.poiLocation

  return (
    <Card sx={{ maxWidth: 600, mx: 'auto' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LocationOn color="error" />
          <Typography variant="h6">Location Revealed</Typography>
        </Box>
        {poi && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {poi.markerEmoji || '📍'}
            </Typography>
            {poi.description && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                {poi.description}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              Check the map to see this location
            </Typography>
          </Box>
        )}
        {!poi && node.description && (
          <Typography variant="body1">{node.description}</Typography>
        )}
      </CardContent>
    </Card>
  )
}

// Image display
function ImageDisplayContent({ node }: { node: SessionNodeDto }) {
  return (
    <Card sx={{ maxWidth: 800, mx: 'auto' }}>
      {node.imageUrl ? (
        <CardMedia
          component="img"
          image={node.imageUrl}
          alt={node.label || 'Image'}
          sx={{ maxHeight: 500, objectFit: 'contain' }}
        />
      ) : (
        <Box
          sx={{
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <ImageIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
        </Box>
      )}
      {(node.imageCaption || node.label) && (
        <CardContent>
          {node.label && (
            <Typography variant="h6" gutterBottom>
              {node.label}
            </Typography>
          )}
          {node.imageCaption && (
            <Typography variant="body2" color="text.secondary">
              {node.imageCaption}
            </Typography>
          )}
        </CardContent>
      )}
    </Card>
  )
}

// Video player
function VideoPlayerContent({ node }: { node: SessionNodeDto }) {
  return (
    <Card sx={{ maxWidth: 800, mx: 'auto' }}>
      {node.videoUrl ? (
        <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
          <video
            src={node.videoUrl}
            controls
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Videocam sx={{ fontSize: 64, color: 'text.disabled' }} />
        </Box>
      )}
      {node.label && (
        <CardContent>
          <Typography variant="h6">{node.label}</Typography>
        </CardContent>
      )}
    </Card>
  )
}

// Audio player
function AudioPlayerContent({ node }: { node: SessionNodeDto }) {
  return (
    <Card sx={{ maxWidth: 600, mx: 'auto' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AudioFile color="primary" />
          <Typography variant="h6">{node.label || 'Audio'}</Typography>
        </Box>
        {node.audioUrl ? (
          <audio src={node.audioUrl} controls style={{ width: '100%' }} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No audio available
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

// Time modifier (bonus/penalty)
function TimeModifierContent({ node }: { node: SessionNodeDto }) {
  return (
    <Card sx={{ maxWidth: 400, mx: 'auto' }}>
      <CardContent sx={{ textAlign: 'center' }}>
        <Schedule sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          {node.label || 'Time Bonus'}
        </Typography>
        {node.description && (
          <Typography variant="body2" color="text.secondary">
            {node.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

// Timer control (pause/resume adventure clock)
function TimerControlContent({ node }: { node: SessionNodeDto }) {
  return (
    <Card sx={{ maxWidth: 400, mx: 'auto' }}>
      <CardContent sx={{ textAlign: 'center' }}>
        <PauseCircle sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          {node.label || 'Adventure Paused'}
        </Typography>
        {node.description && (
          <Typography variant="body2" color="text.secondary">
            {node.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

// Generic fallback
function GenericNodeContent({ node }: { node: SessionNodeDto }) {
  return (
    <Box sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="h5" gutterBottom>
        {node.label}
      </Typography>
      {node.description && (
        <Typography variant="body1" color="text.secondary">
          {node.description}
        </Typography>
      )}
    </Box>
  )
}
